/* global Turbo */
import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

// Stimulus controller for the rule test runner.
// Manages item search/selection and test submission via Turbo Stream.
// Registered as: catalog--rule-test
export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "selectedItem",
    "selectedItemLabel",
    "itemId",
    "testButton",
    "resultPlaceholder",
  ]
  static values = {
    searchUrl: String,
    testUrl: String,
    // CONEK-6236: translated copy comes from the server; never hard-code it here.
    runningLabel: { type: String, default: "…" },
    noResultsLabel: String,
  }

  connect() {
    this._searchTimeout = null
    // The button's server-rendered (translated) label, restored after each run.
    this._runLabel = this.testButtonTarget.textContent.trim()
  }

  search() {
    clearTimeout(this._searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.searchResultsTarget.classList.add("hidden")
      return
    }

    this._searchTimeout = setTimeout(() => this._performSearch(query), 300)
  }

  async _performSearch(query) {
    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}&limit=10`
      const response = await get(url, {
        responseKind: "json",
      })

      if (!response.ok) return

      const data = await response.json
      this._renderResults(data.results || [])
    } catch (_e) {
      // Silently fail on search errors
    }
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _renderResults(items) {
    const container = this.searchResultsTarget

    if (!items.length) {
      container.innerHTML = `<div class="px-3 py-2 text-sm text-muted-foreground">${this._escapeHtml(this.noResultsLabelValue)}</div>`
      container.classList.remove("hidden")
      return
    }

    container.innerHTML = items
      .map((item) => {
        const code = this._escapeHtml(item.code || "")
        const name = this._escapeHtml(item.name || "")
        const label = `${code}${name ? " — " + name : ""}`
        return `
      <button type="button"
              class="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm"
              data-action="click->catalog--rule-test#selectItem"
              data-item-id="${this._escapeHtml(item.id)}"
              data-item-label="${label}">
        <code class="font-mono text-xs bg-muted/50 px-1 rounded">${code}</code>
        <span class="text-muted-foreground truncate">${name}</span>
      </button>
    `
      })
      .join("")

    container.classList.remove("hidden")
  }

  selectItem(event) {
    const button = event.currentTarget
    const itemId = button.dataset.itemId
    const label = button.dataset.itemLabel

    this.itemIdTarget.value = itemId
    this.selectedItemLabelTarget.textContent = label
    this.selectedItemTarget.classList.remove("hidden")
    this.searchResultsTarget.classList.add("hidden")
    this.searchInputTarget.value = ""
    this._setButtonDisabled(false)
  }

  clearSelection() {
    this.itemIdTarget.value = ""
    this.selectedItemTarget.classList.add("hidden")
    this._setButtonDisabled(true)
  }

  // Ui::ButtonComponent renders aria-disabled next to disabled; keep them in
  // sync or assistive tech (and Playwright) still sees a disabled button.
  _setButtonDisabled(disabled) {
    this.testButtonTarget.disabled = disabled
    this.testButtonTarget.setAttribute("aria-disabled", String(disabled))
  }

  async runTest() {
    const itemId = this.itemIdTarget.value
    if (!itemId) return

    this._setButtonDisabled(true)
    this.testButtonTarget.textContent = this.runningLabelValue

    try {
      const response = await post(this.testUrlValue, {
        // A plain object: request.js sends it as JSON. A URLSearchParams body
        // went out labelled application/json too, so Rails never saw item_id
        // and every run answered "Item not found" (CONEK-6236).
        body: { item_id: itemId },
        responseKind: "turbo-stream",
      })

      if (response.ok) {
        const html = await response.text
        Turbo.renderStreamMessage(html)
      }
    } catch (_e) {
      // Error handled by Turbo
    } finally {
      this._setButtonDisabled(false)
      this.testButtonTarget.textContent = this._runLabel
    }
  }
}
